import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import { withStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableHead from '@material-ui/core/TableHead';
import TableCell from '@material-ui/core/TableCell';
import TableFooter from '@material-ui/core/TableFooter';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import React, { Fragment } from 'react';
import { Query } from 'react-apollo';
import gql from 'graphql-tag';
import { LINKS_PER_PAGE, Role_Admin, SHOW_PRODUCT_TABLE_WARRANTY } from '../../constants';
import UserProfile from '../auth/UserProfile';
import theme from '../../theme';
import FbEmptyResult from '../common/FbEmptyResult';
import FbError from '../common/FbError';
import FbLoader from '../common/FbLoader';
import Pagination from '../common/Pagination';
import ModelSearch from '../ModelSearch';
import ProductHeaderRow from '../product/ProductHeaderRow';
import ProductRow from '../product/ProductRow';
import BulkEnrichDialog from '../product/BulkEnrichDialog';
import ProductLineDialog from '../product/ProductLineDialog';
import AdminAddProductDialog from './AdminAddProductDialog';
import Hidden from '@material-ui/core/Hidden';
import Typography from '@material-ui/core/Typography';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Divider from '@material-ui/core/Divider';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Tooltip from '@material-ui/core/Tooltip';
import IconButton from '@material-ui/core/IconButton';
import Popover from '@material-ui/core/Popover';
import ViewColumn from '@material-ui/icons/ViewColumn';
import {
	loadAdminProductsFilters,
	saveAdminProductsFilters,
	loadAdminProductsColumns,
	saveAdminProductsColumns,
} from '../../libs/adminProductsPrefs';

const ALL_PRODUCTS_QUERY = gql`
query get_all_products(
	$manufacturerId: Int,
	$filter: String,
	$skip: Int,
	$first: Int,
	$productType: String,
	$batteryComponentType: String,
	$isSmart: Boolean
) {
	admin(
		manufacturerId: $manufacturerId,
		filter: $filter,
		skip: $skip,
		first: $first,
		productType: $productType,
		batteryComponentType: $batteryComponentType,
		isSmart: $isSmart
	) {
		allProducts {
			productId
			approvedDate
			expiryDate
			fireTested
			model
			displayModelName
			modelNameType
			wattage
			productType
			batteryComponentType
			cecEquipmentCategory
			nominalStorageKwh
			nominalUsableKwh
			nominalVoltage
			nominalMaxPowerKw
			isSmart
			productSource
			brand
			inverterType
			manufacturerName
			manufacturerId
			productLine {
				productLineId
				lineName
			}
			productWarranty {
				componentInYears
				performanceInYears
				componentWarrantyLink
				performanceWarrantyLink
				productWarrantyId
			}
			serialStats {
				failed
				installed
				pending
				totalRequests
			}
		}
		allProductsCount
	}
}
`;

const MANUFACTURERS_QUERY = gql`
query getManufacturersForFilter {
	admin {
		manufacturers {
			manufacturerId
			displayName
		}
	}
}
`;

const styles = {
	root: {
		width: '100%',
		overflowX: 'auto',
	},
	Grid: {
		backgroundColor: theme.palette.grey[100],
		paddingLeft: theme.spacing(2),
		paddingRight: theme.spacing(2),
		minHeight: 56,
		borderLeftWidth: '1px',
		borderRightWidth: '1px',
		borderTopWidth: '1px',
		borderColor: theme.palette.common.borderColor,
	},
	paperTable: {
		border: '1px solid',
		borderColor: theme.palette.common.borderColor,
		minHeight: 'calc(100vh - 300px)',
		width: '100%',
		overflowX: 'auto',
		background: theme.palette.common.white,
	},
	filterControl: {
		minWidth: 140,
		marginRight: 0,
		'& .MuiInputBase-root': {
			fontSize: '0.85rem',
		},
		'& .MuiOutlinedInput-root': {
			height: 40,
			minHeight: 40,
			boxSizing: 'border-box',
		},
		'& .MuiSelect-select': {
			display: 'flex',
			alignItems: 'center',
		},
		'& .MuiInputLabel-outlined': {
			fontSize: '0.8rem',
		},
	},
	filterLabel: {
		fontSize: '0.8rem',
	},
	filterGroupLabel: {
		fontSize: '0.6875rem',
		fontWeight: 600,
		textTransform: 'uppercase',
		letterSpacing: '0.06em',
		color: theme.palette.text.secondary,
		marginRight: 0,
		alignSelf: 'center',
		lineHeight: 1.2,
		whiteSpace: 'nowrap',
	},
	toolbarDivider: {
		width: 1,
		height: 40,
		minHeight: 40,
		alignSelf: 'center',
		marginLeft: 0,
		marginRight: 0,
	},
	manufacturerFilter: {
		minWidth: 200,
		marginRight: 0,
		'& .MuiInputBase-root': {
			fontSize: '0.85rem',
		},
		'& .MuiOutlinedInput-root': {
			height: 40,
			minHeight: 40,
			boxSizing: 'border-box',
		},
		'& .MuiSelect-select': {
			display: 'flex',
			alignItems: 'center',
		},
		'& .MuiInputLabel-outlined': {
			fontSize: '0.8rem',
		},
	},
	toolbarButton: {
		height: 40,
		minHeight: 40,
		maxHeight: 40,
		boxSizing: 'border-box',
		paddingTop: theme.spacing(0.5),
		paddingBottom: theme.spacing(0.5),
		paddingLeft: theme.spacing(1.5),
		paddingRight: theme.spacing(1.5),
		textTransform: 'none',
		fontSize: '0.8125rem',
	},
	pageTitle: {
		fontWeight: 600,
		fontSize: 16,
		padding: theme.spacing(1, 0),
	},
	titleRow: {
		marginBottom: theme.spacing(0.5),
		flexWrap: 'wrap',
	},
	viewPopover: {
		padding: theme.spacing(2),
		minWidth: 280,
	},
	viewOptionLabel: {
		marginRight: theme.spacing(0.5),
		display: 'block',
	},
	toolbarMain: {
		paddingTop: theme.spacing(1),
		paddingBottom: theme.spacing(1),
	},
	toolbarRight: {
		display: 'flex',
		flexWrap: 'wrap',
		alignItems: 'center',
		justifyContent: 'flex-end',
		gap: theme.spacing(1.5),
	},
	expiryLegend: {
		marginTop: theme.spacing(1),
		marginBottom: theme.spacing(1.5),
		paddingLeft: theme.spacing(2),
		paddingRight: theme.spacing(2),
		fontSize: '0.8125rem',
		lineHeight: 1.5,
		color: theme.palette.text.secondary,
		maxWidth: 800,
	},
	manufacturerContext: {
		marginTop: theme.spacing(-0.5),
		marginBottom: theme.spacing(0.5),
		paddingLeft: theme.spacing(2),
	},
};

const FooterCell = withStyles(t => ({
	body: {
		padding: t.spacing(2),
		borderBottomColor: t.palette.grey[200],
		fontSize: 14,
		whiteSpace: 'nowrap',
	},
}))(TableCell);

class AdminProducts extends React.Component {
	constructor(props) {
		super(props);
		const savedF = loadAdminProductsFilters() || {};
		const savedC = loadAdminProductsColumns() || {};
		this.state = {
			pageNumber: 1,
			pageSize: LINKS_PER_PAGE,
			filter: savedF.filter != null ? savedF.filter : null,
			productTypeFilter: savedF.productTypeFilter != null ? String(savedF.productTypeFilter) : '',
			batteryComponentTypeFilter:
				savedF.batteryComponentTypeFilter != null ? String(savedF.batteryComponentTypeFilter) : '',
			isSmartFilter: savedF.isSmartFilter != null ? String(savedF.isSmartFilter) : '',
			manufacturerFilter:
				savedF.manufacturerFilter != null ? Number(savedF.manufacturerFilter) : 0,
			bulkEnrichOpen: false,
			addProductOpen: false,
			currentProducts: [],
			compactTable: !!savedC.compactTable,
			hideSerialsDivider: !!savedC.hideSerialsDivider,
			viewOptionsAnchor: null,
			lineDialogOpen: false,
			lineDialogProductId: null,
			lineDialogLineId: null,
			productTableKey: 0,
		};
	}

	componentDidUpdate(prevProps, prevState) {
		const filterKeys = [
			'manufacturerFilter',
			'productTypeFilter',
			'batteryComponentTypeFilter',
			'isSmartFilter',
			'filter',
		];
		if (filterKeys.some((k) => prevState[k] !== this.state[k])) {
			saveAdminProductsFilters(this.state);
		}
		if (
			prevState.compactTable !== this.state.compactTable
			|| prevState.hideSerialsDivider !== this.state.hideSerialsDivider
		) {
			saveAdminProductsColumns({
				compactTable: this.state.compactTable,
				hideSerialsDivider: this.state.hideSerialsDivider,
			});
		}
	}

	onPageChange = (newPage) => this.setState({ pageNumber: newPage });

	handleSearch = (data) => this.setState({ filter: data, pageNumber: 1 });

	handleProductTypeFilter = (e) =>
		this.setState({ productTypeFilter: e.target.value, pageNumber: 1 });

	handleBatteryComponentTypeFilter = (e) =>
		this.setState({ batteryComponentTypeFilter: e.target.value, pageNumber: 1 });

	handleIsSmartFilter = (e) =>
		this.setState({ isSmartFilter: e.target.value, pageNumber: 1 });

	handleManufacturerFilter = (e) =>
		this.setState({ manufacturerFilter: Number(e.target.value), pageNumber: 1 });

	onProductLineDialogShow = (scroll, model) => {
		const productLineId = model.productLine != null ? model.productLine.productLineId : null;
		this.setState({
			lineDialogOpen: true,
			lineDialogProductId: model.productId,
			lineDialogLineId: productLineId,
		});
	};

	onProductLineDialogClose = () => {
		this.setState((s) => ({
			lineDialogOpen: false,
			lineDialogProductId: null,
			lineDialogLineId: null,
			productTableKey: s.productTableKey + 1,
		}));
	};

	getQueryVariables = () => {
		const { pageNumber, pageSize, filter, productTypeFilter, batteryComponentTypeFilter, isSmartFilter, manufacturerFilter } = this.state;
		const skip = (pageNumber - 1) * pageSize;
		return {
			manufacturerId: manufacturerFilter > 0 ? parseInt(manufacturerFilter) : 0,
			first: pageSize,
			skip,
			filter,
			productType: productTypeFilter || null,
			batteryComponentType: batteryComponentTypeFilter || null,
			isSmart: isSmartFilter === '' ? null : isSmartFilter === 'true',
		};
	};

	render() {
		const { classes } = this.props;
		const role = UserProfile.getRole();
		const showManufacturerColumn = this.state.manufacturerFilter === 0;
		const tableColSpan = (this.state.hideSerialsDivider ? 9 : 10)
			- (showManufacturerColumn ? 0 : 1)
			- (SHOW_PRODUCT_TABLE_WARRANTY ? 0 : 2);

		if (role !== Role_Admin) {
			return (
				<Typography style={{ padding: 32 }} color="error">
					Access denied. Super Admin only.
				</Typography>
			);
		}

		return (
			<Fragment>
				<Grid container className={classes.titleRow} alignItems="center" justify="space-between" spacing={1}>
					<Grid item>
						<Typography className={classes.pageTitle}>All Products</Typography>
						{this.state.manufacturerFilter > 0 ? (
							<Query query={MANUFACTURERS_QUERY} fetchPolicy="cache-first">
								{({ data }) => {
									const list = data?.admin?.manufacturers || [];
									const m = list.find((x) => x.manufacturerId === this.state.manufacturerFilter);
									return m?.displayName ? (
										<Typography
											variant="body2"
											color="textSecondary"
											className={classes.manufacturerContext}
											component="p"
										>
											Manufacturer: {m.displayName}
										</Typography>
									) : null;
								}}
							</Query>
						) : null}
					</Grid>
					<Grid item>
						<Tooltip title="Table view options" placement="bottom-end">
							<IconButton
								size="small"
								aria-label="Table view options"
								onClick={(e) => this.setState({ viewOptionsAnchor: e.currentTarget })}
							>
								<ViewColumn fontSize="small" />
							</IconButton>
						</Tooltip>
						<Popover
							open={Boolean(this.state.viewOptionsAnchor)}
							anchorEl={this.state.viewOptionsAnchor}
							onClose={() => this.setState({ viewOptionsAnchor: null })}
							anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
							transformOrigin={{ vertical: 'top', horizontal: 'right' }}
						>
							<div className={classes.viewPopover}>
								<Tooltip
									title="Less padding per row and slightly smaller warranty buttons on desktop. Phone sizes keep larger tap targets."
									placement="left"
								>
									<FormControlLabel
										className={classes.viewOptionLabel}
										control={(
											<Checkbox
												checked={this.state.compactTable}
												onChange={(e) => this.setState({ compactTable: e.target.checked })}
												color="primary"
											/>
										)}
										label={(
											<Typography variant="body2" color="textSecondary">
												Compact view
											</Typography>
										)}
									/>
								</Tooltip>
								<Tooltip
									title="Hides the narrow divider column between available serials and failed requests so the table is one column wider."
									placement="left"
								>
									<FormControlLabel
										className={classes.viewOptionLabel}
										control={(
											<Checkbox
												checked={this.state.hideSerialsDivider}
												onChange={(e) => this.setState({ hideSerialsDivider: e.target.checked })}
												color="primary"
											/>
										)}
										label={(
											<Typography variant="body2" color="textSecondary">
												Hide divider between serials columns
											</Typography>
										)}
									/>
								</Tooltip>
							</div>
						</Popover>
					</Grid>
				</Grid>

				<Grid
					container
					direction="row"
					justify="space-between"
					alignItems="center"
					className={`${classes.Grid} ${classes.toolbarMain}`}
					spacing={2}
				>
					<Grid item xs={12} md="auto">
						<Grid container direction="row" alignItems="center" spacing={2} wrap="wrap">
							<Grid item>
								<Query query={MANUFACTURERS_QUERY} fetchPolicy="network-only">
									{({ data }) => {
										const manufacturers = data?.admin?.manufacturers || [];
										return (
											<FormControl variant="outlined" size="small" className={classes.manufacturerFilter}>
												<InputLabel className={classes.filterLabel}>Manufacturer</InputLabel>
												<Select
													value={this.state.manufacturerFilter}
													onChange={this.handleManufacturerFilter}
													label="Manufacturer"
												>
													<MenuItem value={0}>All Manufacturers</MenuItem>
													{manufacturers.map((m) => (
														<MenuItem key={m.manufacturerId} value={m.manufacturerId}>
															{m.displayName}
														</MenuItem>
													))}
												</Select>
											</FormControl>
										);
									}}
								</Query>
							</Grid>
							<Grid item>
								<ModelSearch
									layout="toolbar"
									filterText={this.state.filter}
									searchHandler={this.handleSearch}
								/>
							</Grid>
						</Grid>
					</Grid>
					<Grid item xs={12} md>
						<div className={classes.toolbarRight}>
							<Typography component="span" className={classes.filterGroupLabel}>
								Filters
							</Typography>
							<FormControl variant="outlined" size="small" className={classes.filterControl}>
								<InputLabel className={classes.filterLabel}>Product Type</InputLabel>
								<Select
									value={this.state.productTypeFilter}
									onChange={this.handleProductTypeFilter}
									label="Product Type"
								>
									<MenuItem value="">All</MenuItem>
									<MenuItem value="BATTERY">Battery</MenuItem>
									<MenuItem value="INVERTER">Inverter</MenuItem>
								</Select>
							</FormControl>
							<FormControl variant="outlined" size="small" className={classes.filterControl}>
								<InputLabel className={classes.filterLabel}>Component Type</InputLabel>
								<Select
									value={this.state.batteryComponentTypeFilter}
									onChange={this.handleBatteryComponentTypeFilter}
									label="Component Type"
								>
									<MenuItem value="">All</MenuItem>
									<MenuItem value="MODULAR">Modular</MenuItem>
									<MenuItem value="MASTER">Master</MenuItem>
									<MenuItem value="STANDALONE">Standalone</MenuItem>
									<MenuItem value="INVERTER_CONTROLLER">Inverter controller</MenuItem>
								</Select>
							</FormControl>
							<FormControl variant="outlined" size="small" className={classes.filterControl}>
								<InputLabel className={classes.filterLabel}>Is Smart</InputLabel>
								<Select
									value={this.state.isSmartFilter}
									onChange={this.handleIsSmartFilter}
									label="Is Smart"
								>
									<MenuItem value="">All</MenuItem>
									<MenuItem value="true">Yes</MenuItem>
									<MenuItem value="false">No</MenuItem>
								</Select>
							</FormControl>
							<Divider orientation="vertical" className={classes.toolbarDivider} />
							<Button
								variant="outlined"
								color="primary"
								size="small"
								className={classes.toolbarButton}
								onClick={() => this.setState({ bulkEnrichOpen: true })}
							>
								Bulk AI Enrich
							</Button>
							<Button
								variant="contained"
								color="primary"
								size="small"
								className={classes.toolbarButton}
								onClick={() => this.setState({ addProductOpen: true })}
							>
								+ Add Product
							</Button>
						</div>
					</Grid>
				</Grid>

				<Typography className={classes.expiryLegend} component="p">
					Expiry dates: orange = within 90 days; red = already expired. Hover a date for exact days remaining.
				</Typography>

				<div className={classes.root}>
					<Paper elevation={0} square classes={{ root: classes.paperTable }}>
						<Table>
							<Hidden smDown>
								<TableHead>
									<ProductHeaderRow
										adminView={true}
										showManufacturer={showManufacturerColumn}
										hideSerialsDivider={this.state.hideSerialsDivider}
										showWarranty={SHOW_PRODUCT_TABLE_WARRANTY}
									/>
								</TableHead>
							</Hidden>

							<Query
								key={this.state.productTableKey}
								query={ALL_PRODUCTS_QUERY}
								variables={this.getQueryVariables()}
								fetchPolicy="no-cache"
							>
								{({ loading, error, data }) => {
									if (loading) return <FbLoader isTableBodyRequired={true} colSpan={tableColSpan} loaderStyle="emptycell" />;
									if (error) return <FbError isTableBodyRequired={true} colSpan={tableColSpan} errorStyle="emptycell" />;

									const products = data?.admin?.allProducts || [];
									const productCount = data?.admin?.allProductsCount || 0;

									if (products !== this.state.currentProducts) {
										setTimeout(() => this.setState({ currentProducts: products }), 0);
									}

									const paging = (
										<Pagination
											Prefix="products"
											TotalRecords={productCount}
											PageSize={this.state.pageSize}
											PageNumber={this.state.pageNumber}
											ShowPreviousNext={true}
											onPageChange={this.onPageChange}
										/>
									);

									const listModels = products.map((model, index) => (
										<ProductRow
											key={model.productId || index}
											role={role}
											isDistributor={false}
											adminView={true}
											model={model}
											index={index}
											showManufacturer={showManufacturerColumn}
											shouldOpenDialog={this.onProductLineDialogShow}
											compactTable={this.state.compactTable}
											hideSerialsDivider={this.state.hideSerialsDivider}
											showWarranty={SHOW_PRODUCT_TABLE_WARRANTY}
										/>
									));

									const result = products.length > 0
										? listModels
										: <FbEmptyResult isTableBodyRequired={false} colSpan={tableColSpan} errorStyle="emptycell" />;

									const paginationContent = products.length > 0 && productCount > this.state.pageSize
										? <TableFooter><TableRow><FooterCell colSpan={String(tableColSpan)}>{paging}</FooterCell></TableRow></TableFooter>
										: null;

									return (
										<Fragment>
											<TableBody>{result}</TableBody>
											{paginationContent}
										</Fragment>
									);
								}}
							</Query>
						</Table>
					</Paper>
				</div>

				<BulkEnrichDialog
					open={this.state.bulkEnrichOpen}
					onClose={() => this.setState({ bulkEnrichOpen: false })}
					products={this.state.currentProducts || []}
				/>

				<AdminAddProductDialog
					open={this.state.addProductOpen}
					onClose={() => this.setState({ addProductOpen: false })}
				/>

				<ProductLineDialog
					open={this.state.lineDialogOpen}
					handleClose={this.onProductLineDialogClose}
					selectedProductId={this.state.lineDialogProductId}
					selectedProductLineId={this.state.lineDialogLineId}
					notifyUpdate={() => {}}
				/>
			</Fragment>
		);
	}
}

export default withStyles(styles)(AdminProducts);
